import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, ChevronRight } from 'lucide-react';
import { fetchPost, fetchPosts } from '@/lib/api';

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function readTime(content) {
  if (!content) return '1 min read';
  const words = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
  const mins = Math.max(1, Math.ceil(words / 200));
  return `${mins} min read`;
}

function PostSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="bg-brand-lavender h-72 md:h-[28rem] mb-8" />
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <div className="bg-brand-lavender rounded h-4 w-32 mb-6" />
        <div className="bg-brand-lavender rounded h-12 w-3/4 mb-4" />
        <div className="bg-brand-lavender rounded h-4 w-48 mb-12" />
        <div className="space-y-3">
          <div className="bg-brand-lavender rounded h-4 w-full" />
          <div className="bg-brand-lavender rounded h-4 w-full" />
          <div className="bg-brand-lavender rounded h-4 w-5/6" />
          <div className="bg-brand-lavender rounded h-4 w-full" />
          <div className="bg-brand-lavender rounded h-4 w-3/4" />
        </div>
      </div>
    </div>
  );
}

function NotFound() {
  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-24 text-center">
      <h1 className="text-4xl font-black text-black mb-4 uppercase">
        Post Not Found
      </h1>
      <p className="text-black/60 mb-8 text-lg">
        This article doesn't exist or has been removed.
      </p>
      <Link to="/blog" className="btn-lime rounded-full inline-flex items-center gap-2">
        <ArrowLeft className="w-4 h-4" />
        Back to Blog
      </Link>
    </div>
  );
}

function RelatedPosts({ currentSlug }) {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    fetchPosts()
      .then((all) => setPosts(all.filter((p) => p.slug !== currentSlug).slice(0, 3)))
      .catch(() => {});
  }, [currentSlug]);

  if (posts.length === 0) return null;

  return (
    <section className="py-16 md:py-24 section-lavender">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl font-black text-black uppercase">
            More Articles
          </h2>
          <Link
            to="/blog"
            className="text-sm font-bold text-black/60 hover:text-black inline-flex items-center gap-1 transition-colors"
          >
            View all <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {posts.map((post) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="group block"
            >
              {post.cover_image ? (
                <div className="overflow-hidden rounded-2xl border-2 border-black mb-4">
                  <img
                    src={post.cover_image}
                    alt={post.title}
                    className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
              ) : (
                <div className="w-full h-44 bg-white rounded-2xl border-2 border-black mb-4 flex items-center justify-center">
                  <span className="font-black text-xl text-black/10 uppercase">CatchingOn</span>
                </div>
              )}
              <h3 className="text-lg font-black text-black group-hover:text-black/70 transition-colors uppercase">
                {post.title}
              </h3>
              <p className="text-sm text-black/50 mt-1">
                {formatDate(post.publishedAt)}
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    fetchPost(slug)
      .then(setPost)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <PostSkeleton />;
  if (notFound || !post) return <NotFound />;

  return (
    <article data-testid="blog-post-page">
      {/* Cover image — full-width hero */}
      {post.cover_image && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="relative w-full h-72 md:h-[28rem] overflow-hidden"
        >
          <img
            src={post.cover_image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-periwinkle/80 via-transparent to-transparent" />
        </motion.div>
      )}

      {/* Post header */}
      <section className={`section-periwinkle ${post.cover_image ? '-mt-24 relative z-10' : 'pt-16 md:pt-24'}`}>
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {/* Back link */}
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm text-black/50 hover:text-black transition-colors mb-6 font-bold"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to blog
            </Link>

            {/* Tags */}
            {post.tags?.length > 0 && (
              <div className="flex flex-wrap gap-3 mb-4">
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs font-bold uppercase tracking-wider text-black/50"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Title */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-black mb-6 leading-tight uppercase">
              {post.title}
            </h1>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-black/50 pb-8 border-b-2 border-black/10">
              {post.author && (
                <span className="font-semibold text-black">{post.author}</span>
              )}
              {post.publishedAt && (
                <>
                  <span>&middot;</span>
                  <span>{formatDate(post.publishedAt)}</span>
                </>
              )}
              <span>&middot;</span>
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {readTime(post.content)}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Post content */}
      <section className="section-periwinkle py-10 md:py-14">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div
              className="prose prose-lg max-w-none
                prose-headings:font-black prose-headings:text-black prose-headings:uppercase
                prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                prose-p:text-black/70 prose-p:leading-relaxed
                prose-a:text-black prose-a:underline prose-a:decoration-brand-lime prose-a:decoration-2 prose-a:underline-offset-2 hover:prose-a:decoration-4
                prose-img:rounded-2xl prose-img:border-2 prose-img:border-black
                prose-blockquote:border-brand-lime prose-blockquote:bg-white/50 prose-blockquote:rounded-r-xl prose-blockquote:py-1 prose-blockquote:px-6
                prose-strong:text-black
                prose-code:text-black prose-code:bg-brand-lime/30 prose-code:rounded prose-code:px-1.5 prose-code:py-0.5 prose-code:before:content-none prose-code:after:content-none
                prose-pre:bg-black prose-pre:text-white prose-pre:rounded-xl
                [&_img[data-align=left]]:mr-auto [&_img[data-align=left]]:ml-0
                [&_img[data-align=center]]:mx-auto
                [&_img[data-align=right]]:ml-auto [&_img[data-align=right]]:mr-0
                [&_img]:block"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </motion.div>
        </div>
      </section>

      {/* Author / share bar */}
      <section className="section-periwinkle pb-8">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="border-t-2 border-b-2 border-black/10 py-6 flex items-center justify-between">
            <div>
              {post.author && (
                <p className="text-sm">
                  <span className="text-black/50">Written by </span>
                  <span className="font-bold text-black">{post.author}</span>
                </p>
              )}
            </div>
            <Link
              to="/blog"
              className="text-sm font-bold text-black/60 hover:text-black inline-flex items-center gap-1 transition-colors"
            >
              More articles <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Related posts */}
      <RelatedPosts currentSlug={slug} />

      {/* CTA */}
      <section className="py-16 md:py-24 bg-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl md:text-4xl font-black text-white mb-6 uppercase">
              Need Help With Your Marketing?
            </h2>
            <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto">
              From content creation to social media management — let's build a
              strategy that gets results.
            </p>
            <Link to="/contact" className="btn-lime rounded-full inline-block text-lg">
              Get in Touch
            </Link>
          </motion.div>
        </div>
      </section>
    </article>
  );
};

export default BlogPost;
